import {
  AutoCompleteUncontrolled,
  ISelectType,
  Table,
  useFetch,
  useRole,
} from "@4uhub/lib4uhub";
import { useMediaQuery, useTheme } from "@mui/material";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { IProtocolGenerate } from "../../../../models/protocol-generate";
import { IGetTicketServiceMessages } from "../../../../models/ticket-service-message";
import MainApiService from "../../../../services/mainApi.service";
import TicketServService from "../../../../services/ticket.service";

const TICKET_SERVICE_MESSAGE_ROUTE =
  process.env.REACT_APP_TICKETS + "/api/v1/TicketServiceMessage";
const ticketServMessageService = new MainApiService<IGetTicketServiceMessages>(
  TICKET_SERVICE_MESSAGE_ROUTE
);
const GENERICS_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/Generic/";
const genericsService = new MainApiService<ISelectType>(GENERICS_ROUTE);

const translationPath = "chatBot.page.service.ticketServiceMessage.";

const ticketService = new TicketServService();

const TicketServiceMessagePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { eI } = useParams();

  const createPermission = useRole(["Create"]);

  const [target, setTarget] = useState<ISelectType | null>(null);

  const [targetOptions, setTargetOptions] = useState<ISelectType[]>([]);

  const theme = useTheme();

  const matches = useMediaQuery(theme.breakpoints.down("sm"));

  const { sendRequest } = useFetch(genericsService.list);

  const { sendRequest: ticketServiceList } = useFetch(
    ticketService.listTicketServiceInMessages
  );
  const [ticketServicesOptions, setTicketServiceOptions] = useState<
    ISelectType[]
  >([]);
  const [ticketServiceSelected, setTicketServiceSelected] =
    useState<ISelectType | null>(null);

  const fetchTicketServiceOptions = useCallback(async () => {
    const { data, success } = await ticketServiceList(undefined);
    if (data && success) {
      setTicketServiceOptions(data);
    }
  }, [ticketServiceList]);

  const fetchTargetOptions = useCallback(async () => {
    const { data, success } = await sendRequest({
      Identifier: "TicketServiceMessageTarget",
    });
    if (data && success) {
      setTargetOptions(data);
    }
  }, [sendRequest]);

  const columns: GridColDef<IGetTicketServiceMessages>[] = [
    {
      field: "message",
      headerName: t(translationPath + "message") || "Message",
    },
    {
      field: "messageType",
      headerName: t(translationPath + "message_type") || "Message type",
    },
    {
      field: "ticketService",
      headerName: t(translationPath + "ticket_service") || "Ticket service",
      renderCell: (params) => params.value.name,
    },
  ];

  const handleRowClick = (params: GridRowParams<IProtocolGenerate>) => {
    navigate(`${params.id.toString()}/edit`);
  };

  const handleAdd = () => {
    navigate("new");
  };

  const props = useMemo(() => {
    return {
      TicketServiceId: ticketServiceSelected
        ? ticketServiceSelected.id
        : undefined,
      targetId: target ? target.id : undefined,
    };
  }, [target, ticketServiceSelected]);

  useEffect(() => {
    fetchTargetOptions();
    fetchTicketServiceOptions();
  }, [fetchTargetOptions, fetchTicketServiceOptions]);

  return (
    <Table<IGetTicketServiceMessages>
      showDefaultMacroFilters={false}
      macroFilters={() => (
        <>
          <AutoCompleteUncontrolled
            onValueSelected={setTarget}
            value={target}
            label={t(translationPath + "target")}
            options={targetOptions}
            sx={{ mt: matches ? 2 : 0, width: 300 }}
            size="small"
            fullWidth
          />
          <AutoCompleteUncontrolled
            onValueSelected={setTicketServiceSelected}
            value={ticketServiceSelected}
            label={t(translationPath + "ticket_service")}
            options={ticketServicesOptions}
            sx={{ mt: matches ? 2 : 0, width: 300 }}
            size="small"
            fullWidth
          />
        </>
      )}
      service={ticketServMessageService}
      serviceProps={props}
      columns={columns}
      title={t(translationPath + "ticket_service_messages")}
      searchInput
      onRowClick={handleRowClick}
      searchInputPlaceHolder={
        t(translationPath + "search") || "Search for a message"
      }
      onAdd={handleAdd}
      showButton={createPermission}
      addButtonLabel={
        t(translationPath + "add_message") || "New message"
      }
      showDeleteButton={false}
      defaultActions={false}
      confirmOptions={{
        title: t(translationPath + "confirm.title"),
        description: t(translationPath + "confirm.description"),
        confirmationButtonProps: {
          variant: "contained",
        },
      }}
    />
  );
};

export default TicketServiceMessagePage;
